// single point of entry for polyfills
import './polyfills';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import BannerContainer from 'components/BannerContainer.jsx';
import UserModalsContainer from 'components/Navigation/UserModalsContainer.jsx';

/*
/!\
- This needs to be called before pageload and not in a setTimeout:
  http://stackoverflow.com/questions/31289530/load-ios-smart-app-banner-through-javascript
*/
import removeAppStoreBanner from './removeAppStoreBanner';
removeAppStoreBanner(navigator.userAgent, window.location.href);

import './config/loadConfig';
import App from './app';
import $ from 'jquery';
import { createTranslator } from 'modules/i18n';
import { findLanguageByShortcut, findLanguageByCode } from 'models/locales.js';
import Globalize from 'globalize';
import moment from 'moment';
import logger from 'modules/logger';

import './modules/core/coreRouter';

let language;

import configureStore from '../../store/configureStore';
import reducerRegistry from '../../reducers/ReducerRegistry';
import coreReducers from '../../reducers/coreReducers';

const activateRedux = language_ => {
    reducerRegistry.register(coreReducers);

    const { store, reload } = configureStore(reducerRegistry)({
        locale: language_,
    });

    window.reduxStore = store;

    reducerRegistry.setChangeListener(reload);

    // client side effects ...
    let previousState = store.getState();
    store.subscribe(() => {
        const nextState = store.getState();

        if (previousState.visiblePanel !== nextState.visiblePanel) {
            document.body.classList[!!nextState.visiblePanel ? 'add' : 'remove']('is-locked');
        }

        previousState = nextState;
    });

    if (module.hot) {
        // Enable Webpack hot module replacement for core reducers
        module.hot.accept('../../reducers/coreReducers', () => {
            reducerRegistry.register(coreReducers);
        });
    }
};

function processData(data) {
    const translations = {};
    translations[language] = data;
    window.Translator = createTranslator(data, language);

    App.start({
        locale: language,
    });
}

function clientLocaleConfiguration(_language, acceptLanguage) {
    Globalize.culture(_language);
    $.ajaxSetup({
        headers: {
            'Accept-Language': acceptLanguage,
        },
    });
    moment.locale(_language);
}

if (!___DEV___) {
    // window.language should come from the processed indexes.html
    language = window.language;
    const locale = findLanguageByShortcut(language);
    const code = locale ? locale.code : language;

    activateRedux(language);

    clientLocaleConfiguration(language, code);
    const i18nTag = document.querySelector('meta[name="i18n-tag"]').content;
    $.getJSON(
        `${process.env.I18N_ROOT}/i18n/${code}/front/messages.json?tag=${encodeURIComponent(
            i18nTag
        )}`
    )
        .done(processData)
        .fail(err => {
            logger.error(err);
        });
} else {
    const match = location.pathname.match('/([a-z]{2}(?:-[A-Z]{2})?)/');
    if (!match) {
        window.location = '/fr/login';
    }
    const locale = match[1];

    // will find locale if using '/fr/' form
    let foundLocale = findLanguageByShortcut(locale);
    // will find locale if using '/fr-FR/' form
    if (!foundLocale) {
        foundLocale = findLanguageByCode(locale);
    }

    if (!foundLocale) {
        throw new Error(`No language found matching ${locale}`);
    }

    const code = foundLocale.code;
    const shortCode = foundLocale.shortcut;

    language = window.language = locale;

    activateRedux(language);

    clientLocaleConfiguration(shortCode || code, code);
    const tag = process.env.I18N_TAG || new Date().getTime();

    $.getJSON(`${process.env.I18N_ROOT}/i18n/${foundLocale.code}/front/messages.json?tag=${tag}`)
        .done(processData)
        .fail(err => {
            logger.error(err);
        });
}

ReactDOM.render(
    <Provider store={window.reduxStore}>
        <BannerContainer />
    </Provider>,
    document.getElementById('banner-region')
);

ReactDOM.render(
    <Provider store={window.reduxStore}>
        <UserModalsContainer i18n={window.language} />
    </Provider>,
    document.getElementById('fullscreen-modal')
);

// inject crowdin just in place translation tool
if (language === 'xh-ZA' || (___DEV___ && language === 'xh')) {
    window._jipt = [];
    window._jipt.push(['project', 'foodassembly']);
    const resource = document.createElement('script');
    resource.src = '//cdn.crowdin.com/jipt/jipt.js';
    document.body.appendChild(resource);
}
